import React from "react";
import { getLeagueStatus, stripHtml } from "./leagueStatus";
import { memberCount } from "./inviteCode";
import { durationLabel } from "./leagueFormat";
import {
  ShieldLockIcon,
  ClockIcon,
  SmallCalendarIcon,
  SmallPieIcon,
  SmallUsersIcon,
  SmallUserIcon,
} from "./icons";

// This screen used to carry its OWN copy of the duration maths — a second
// `weeksBetween` that printed "6 Weeks" / "<1 Week" beside leagueFormat's
// identical one. Client Rev 2 asked for days ("1 day, 7 days, 30 days") and to
// "get rid of <1 Week entirely", and a fix applied to one copy would have left
// the other printing weeks on this card. Now there is one function, in
// leagueFormat, and DURATION reads the same wherever it appears.

const Stat = ({ icon, label, value }) => (
  <div className="flex flex-col items-center text-center px-1">
    <span className="mb-1">{icon}</span>
    <span className="text-xs inter font-semibold text-[#22A36B]">{label}</span>
    <span className="mt-0.5 text-sm inter font-medium text-[#0F172A] truncate max-w-full">
      {value || "—"}
    </span>
  </div>
);

const LeagueDetailCard = ({ game }) => {
  if (!game) return null;
  const status = getLeagueStatus(game);
  // Handle only — QUSHi never displays a user's real name.
  const creatorName = game.user?.user_name || "—";
  const duration = durationLabel(game);
  // get-game-from-slug hands back the members as a relation, not a count.
  const members = memberCount(game);

  const statusLabel = (() => {
    if (!status) return "";
    if (status.tone === "live") return "Live now";
    if (status.tone === "ended") return "Ended";
    if (status.tone === "soon") return `Starts in ${status.countdownValue}`;
    return `Starts in ${status.countdownValue}`;
  })();

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4 md:p-5">
      <div className="flex items-start gap-4">
        <div className="shrink-0">
          <ShieldLockIcon className="w-20 h-20 md:w-24 md:h-24" />
        </div>
        <div className="flex-1 min-w-0">
          {statusLabel && (
            <p className="inline-flex items-center gap-1.5 text-sm text-[#66748C] dm-sans">
              <ClockIcon className="w-3.5 h-3.5" /> {statusLabel}
            </p>
          )}
          <h2 className="mt-1 inter font-bold text-xl md:text-2xl text-[#0F172A] leading-tight">
            {game.title}
          </h2>
          <p className="mt-0.5 text-sm text-[#0F172A] dm-sans">Private League</p>
          {game.description && (
            <p className="mt-3 text-sm text-[#0F172A] dm-sans leading-relaxed">
              {stripHtml(game.description)}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-[#E5E7EB] grid grid-cols-4 gap-2">
        <Stat icon={<SmallCalendarIcon />} label="Duration" value={duration} />
        <Stat icon={<SmallPieIcon />} label="Asset Type" value="All Assets" />
        <Stat
          icon={<SmallUsersIcon />}
          label="Members"
          value={`${members} member${members === 1 ? "" : "s"}`}
        />
        <Stat icon={<SmallUserIcon />} label="Created by" value={creatorName} />
      </div>
    </div>
  );
};

export default LeagueDetailCard;
